import ufeApi from 'services/api/ufeApi';
import urlUtils from 'utils/Url';
import Storage from 'utils/localStorage/Storage';

/**
 * Generates the body for the audit notes service
 * @param {string} commentType - 'order' or 'profile'
 * @param {string} commentDescription - The note description
 * @param {string} orderId - Order ID (required for order type)
 * @returns {object} Request body for audit notes service
 */
const generateAuditNotesServiceBody = (commentType, commentDescription, orderId) => {
    const profileId = Storage.local.getItem('biAccountId');
    const agentInfo = Storage.local.getItem('agentInfo');
    const timeStamp = new Date().toISOString().split('.')[0] + 'Z';

    return {
        timeStamp: timeStamp,
        agentId: agentInfo?.cccId,
        type: 'Note',
        visibleBy: 'All',
        profileId: profileId,
        entityType: commentType === 'order' ? 'Order' : 'Profile',
        entityKey: commentType === 'order' ? orderId : profileId,
        body: {
            note: commentDescription,
            noteAction: null,
            onlineCreditRefundId: null
        },
        generateBy: 'manual',
        source: 'Mirror'
    };
};

/**
 * Adds a note (comment) for either an order or profile
 * @param {string} commentType - 'order' or 'profile'
 * @param {string} commentDescription - The note description
 * @returns {Promise} Resolves with response data or rejects with error
 */
const addNote = async (commentType, commentDescription) => {
    const enableAuditNoteService = Sephora.configurationSettings?.enableAuditNoteService || false;
    const requestUrl = enableAuditNoteService ? '/gway/audit-note' : '/api/util/comments';
    let orderId = '';

    if (commentType === 'order') {
        const urlParams = urlUtils.getParams();
        orderId = urlParams.orderId?.[0] || '';
    }

    const body = !enableAuditNoteService
        ? { commentType, commentDescription, orderId }
        : generateAuditNotesServiceBody(commentType, commentDescription, orderId);

    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    };

    return ufeApi
        .makeRequest(requestUrl, options)
        .then(response => {
            // Check for HTTP error statuses (404, 503, etc.), errorCode, or errors array
            if (response?.responseStatus >= 400 || response?.errorCode || response?.errors) {
                const errorMessage =
                    response?.errorMessages?.join(', ') ||
                    response?.errors?.[0]?.message ||
                    response?.message ||
                    'Message was not saved. Please try again.';

                return Promise.reject(new Error(errorMessage));
            }

            return response;
        })
        .catch(error => {
            // If error is already an Error object, re-throw it
            if (error instanceof Error) {
                throw error;
            }

            // Otherwise, wrap it in an Error object
            throw new Error(error?.message || 'Message was not saved. Please try again.');
        });
};

export default addNote;
